/**
 * ACT2-1124: the confirmation before Connect copies a Redshift IAM account of another workspace. The copy uses the
 * original's IAM role, so the role's trust policy must name this workspace too.
 */
import { Button } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';

import { accountRowIdentity } from '../warehouseAccount/adopt/adoptPresentation';
import { IamTrustPolicyNotice } from './IamTrustPolicyNotice';
import type { OrganizationWarehouseAccounts } from './useOrganizationWarehouseAccounts';

export function CopyAccountDialog({
  organization,
}: {
  organization: OrganizationWarehouseAccounts;
}): React.ReactElement {
  const { confirming, copyPending } = organization;
  const title =
    confirming?.warehouse != null ? accountRowIdentity({ ...confirming.warehouse, name: confirming.name }).title : null;
  return (
    <Dialog
      open={confirming !== null}
      onOpenChange={(open) => {
        if (!open) organization.cancelConfirm();
      }}
    >
      <DialogContent
        onEscapeKeyDown={(event) => {
          if (copyPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (copyPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Connect {title ?? 'the account'}?</DialogTitle>
          <DialogDescription>
            Lookout copies the account from {confirming?.workspaceName ?? 'its workspace'} into this workspace, then
            connects the copy. The copy uses the same IAM role as the original account.
          </DialogDescription>
        </DialogHeader>
        {confirming?.iamTrustPolicy ? <IamTrustPolicyNotice {...confirming.iamTrustPolicy} /> : null}
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            label="Cancel"
            disabled={copyPending}
            onClick={organization.cancelConfirm}
          />
          <Button
            type="button"
            variant="primary"
            label="Copy and connect"
            loading={copyPending}
            onClick={organization.confirm}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
